'use client';

import React, { useState, useEffect } from "react";

type ButtonProps = {
    roomName: string;
    onSelect: (data: SelectedRooms | null) => void;
    defaultCount?: number;
    disabled?: boolean;
    onError: (message: string) => void;
    hideRemoveButton?: boolean;
    value?: [];
};

type SelectedRooms = {
    roomName: string;
    count: number;
    value?: [];
};

type RoomsProps = {
    onSelectedRoomsChange: (selectedRooms: SelectedRooms[]) => void;
    selectedRoomsParent: SelectedRooms[];
    disabled?: boolean;
    setIsFormDirty: any;
};
const ToggleButton: React.FC<ButtonProps> = ({
    roomName,
    onSelect,
    defaultCount = 0,
    disabled = false,
    hideRemoveButton = false,
    onError,
    value
}) => {
    const [isSelected, setIsSelected] = useState<boolean>(defaultCount > 0);
    const [count, setCount] = useState<number>(defaultCount);
    const [inputValue, setInputValue] = useState<string>(defaultCount.toString());

    useEffect(() => {
        setInputValue(defaultCount.toString());
        setCount(defaultCount);
        setIsSelected(defaultCount > 0);
    }, [defaultCount]);

    const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        if (isSelected) {
            setIsSelected(false);
            setCount(0);
            setInputValue("0");
            onSelect({ roomName, count: 0, value });
            onError("");
        } else {
            setIsSelected(true);
            setCount(1);
            setInputValue("1");
            onSelect({ roomName, count: 1, value });
            onError("");
        }
    };

    const handleIncrement = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        if (count < 999) {
            const newCount = count + 1;
            setCount(newCount);
            setInputValue(newCount.toString());
            onSelect({ roomName, count: newCount, value });
            onError("");
        } else {
            onError("Maximum count of 999 reached.");
            setTimeout(() => onError(""), 3000);
        }
    };

    const handleDecrement = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        if (count > 1) {
            const newCount = count - 1;
            setCount(newCount);
            setInputValue(newCount.toString());
            onSelect({ roomName, count: newCount, value });
            onError("");
        } else if (count === 1) {
            setIsSelected(false);
            setCount(0);
            setInputValue("0");
            onSelect({ roomName, count: 0, value });
            onError("");
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        if (raw === "") {
            setInputValue("");
            return;
        }

        const sanitized = raw.replace(/[^0-9]/g, "");
        if (sanitized === "0") return;
        setInputValue(sanitized);
    };

    const handleInputBlur = () => {
        const parsed = parseInt(inputValue, 10);
        if (!inputValue || isNaN(parsed) || parsed < 1) {
            setIsSelected(false);
            setCount(0);
            setInputValue("0");
            onSelect({ roomName, count: 0, value });
            onError("");
        } else if (parsed > 999) {
            onError("Maximum count of 999 reached.");
            setTimeout(() => onError(""), 3000);
            setInputValue(count.toString());
        } else {
            setCount(parsed);
            setInputValue(parsed.toString());
            onSelect({ roomName, count: parsed, value });
            onError("");
        }
    };

    return (
        <div
            className={`flex items-center cursor-pointer justify-center gap-[4px] ${isSelected ? "bg-black text-white px-[18px] py-[4.5px] rounded-full" : ""
                }`}
        >
            {isSelected ? (
                <>
                    <span
                        className={`text-lg ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
                    >
                        ✓
                    </span>
                    {!hideRemoveButton && (
                        <button
                            className="mb-[5px] text-xl text-white rounded-full w-[6px] h-[6px] flex items-center justify-center cursor-pointer"
                            onClick={handleDecrement}
                            disabled={disabled}
                        >
                            -
                        </button>
                    )}

                    <div className="flex items-center gap-[2px]">
                        <span className="fontSize14-sm">{roomName} x</span>
                        <input
                            type="number"
                            value={inputValue}
                            onChange={handleInputChange}
                            onBlur={handleInputBlur}
                            className="w-[40px] text-black px-[2px] py-[1px] rounded border text-center"
                            disabled={disabled}
                        />
                    </div>

                    <button
                        className="text-xl text-white rounded-full w-[6px] h-[6px] flex items-center justify-center cursor-pointer"
                        onClick={handleIncrement}
                        disabled={disabled}
                    >
                        +
                    </button>
                </>
            ) : (
                <button
                    className="border-2 text-black fontSize14-sm px-[18px] py-[4.5px] rounded-full border-[#e5e7eb] cursor-pointer"
                    onClick={handleToggle}
                    disabled={disabled}
                >
                    {roomName}
                </button>
            )}
        </div>
    );
};

const RoomsDetails: React.FC<RoomsProps> = ({ onSelectedRoomsChange, setIsFormDirty, selectedRoomsParent, disabled = false }) => {
    const [rooms, setRooms] = useState<string[]>([
        "Main Bathroom",
        "Ensuite Bathroom",
        "Guest Bathroom",
        "Powder Room",
        "Commercial Bathroom",
        "Kitchen",
        "Laundry",
        "Alfresco",
        "Other",
    ]);
    const [selectedRooms, setSelectedRooms] = useState<SelectedRooms[]>([]);
    const [showInput, setShowInput] = useState(false);
    const [newRoom, setNewRoom] = useState("");
    const [key, setKey] = useState<number>(0);
    const [errorMessage, setErrorMessage] = useState<string>("");

    useEffect(() => {
        onSelectedRoomsChange(selectedRooms);
    }, [selectedRooms, onSelectedRoomsChange]);

    useEffect(() => {
        if (selectedRoomsParent?.length) {
            setSelectedRooms([...selectedRoomsParent]);
            const newRooms = selectedRoomsParent
                .map((room) => room.roomName)
                .filter((roomName) => !rooms.includes(roomName));

            if (newRooms.length) {
                setRooms((prevRooms) => {
                    const withoutOther = prevRooms.filter(room => room !== "Other");
                    return [...withoutOther, ...newRooms, "Other"];
                });
            }
            setKey((prevKey) => prevKey + 1);
        }
    }, [selectedRoomsParent]);

    const handleSelect = (data: SelectedRooms | null) => {
        setErrorMessage('');
        setIsFormDirty(true);
        if (!data || data.count < 1) {
            setSelectedRooms((prev) =>
                prev.filter((item) => item.roomName !== data?.roomName)
            );
            return;
        }
        setSelectedRooms((prev) => {
            const existing = prev.find((item) => item.roomName === data.roomName);
            if (existing) {
                return prev.map((item) =>
                    item.roomName === data.roomName ? data : item
                );
            }
            return [...prev, data];
        });
    };

    const handleOtherSelect = () => {
        setShowInput(true);
    };

    const normalizeName = (name: string) => {
        return name
            .toLowerCase()
            .replace(/\s+/g, '')
            .replace(/\d+$/, '');
    };

    const isDuplicate = (newRoom: string, rooms: string[]) => {
        const normalizedNew = normalizeName(newRoom);
        return rooms.some((room) => normalizeName(room) === normalizedNew);
    };
    const toTitleCase = (str: string): string => {
        return str
            .toLowerCase()
            .split(' ')
            .filter(word => word.trim() !== '')
            .map(word => word[0].toUpperCase() + word.slice(1))
            .join(' ');
    };
    const handleAddButton = () => {
        setErrorMessage("");

        const trimmedRoom = newRoom.trim();
        if (!trimmedRoom) return;

        const checkIsDuplicate = isDuplicate(toTitleCase(trimmedRoom), rooms);
        if (checkIsDuplicate) {
            setErrorMessage('Room already exists!');
            setTimeout(() => setErrorMessage(''), 5000);
            return;
        }
        setRooms((prev) => [...prev.filter((b) => b !== "Other"), toTitleCase(trimmedRoom), "Other"]);
        setShowInput(false);
        setNewRoom("");

    };

    const handleInputCancel = () => {
        setErrorMessage("");
        setShowInput(false);
        setNewRoom("");
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const regex = /[^a-zA-Z0-9\s-]/g;
        const sanitizedValue = e.target.value.replace(regex, '');
        setNewRoom(sanitizedValue);
    };

    return (
        <div className="p-[6px] space-y-[4px]">
            <div className="flex flex-wrap gap-6">
                {rooms.map((roomName) => {
                    const defaultCount =
                        selectedRooms.find((item) => item.roomName === roomName)?.count || 0;

                    const preSelectedRoom =
                        selectedRooms.find((item) => item.roomName === roomName)?.value || [];

                    const hideRemoveButton =
                        selectedRoomsParent.some(
                            (room) => room.roomName === roomName
                        );

                    return roomName === "Other" ? (
                        <div key={roomName}>
                            {showInput ? (
                                <div className="items-center space-x-[2px]">
                                    <input
                                        type="text"
                                        className="border px-[13.5px] py-[9px] rounded mb-[10px] text-[18px]"
                                        placeholder="Enter new room"
                                        value={newRoom}
                                        onChange={handleChange}
                                        maxLength={30}
                                    />
                                    <button
                                        className="text-green-600 bg-white cursor-pointer border p-[10px] rounded  mb-[10px]"
                                        onClick={handleAddButton}
                                        type="button"
                                    >
                                        Create room
                                    </button>
                                    <button
                                        className="p-[10px] bg-[#E5E7EB] cursor-pointer border rounded  mb-[10px]"
                                        onClick={handleInputCancel}
                                        type="button"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            ) : (
                                <ToggleButton
                                    roomName={roomName}
                                    onSelect={() => handleOtherSelect()}
                                    disabled={disabled}
                                    onError={setErrorMessage}
                                    value={preSelectedRoom}
                                />
                            )}
                        </div>
                    ) : (
                        <ToggleButton
                            key={`${roomName}-${key}`}
                            roomName={roomName}
                            onSelect={handleSelect}
                            defaultCount={defaultCount}
                            disabled={disabled}
                            hideRemoveButton={hideRemoveButton}
                            onError={setErrorMessage}
                            value={preSelectedRoom}
                        />
                    );
                })}
            </div>
            {errorMessage && <div className="text-red-500 text-sm">{errorMessage}</div>}
        </div>
    );
};

export default RoomsDetails;
